import { useEffect, useRef } from 'react'

type Params = {
  /** 選択中のひな形 ID */
  templateIds: number[]
  /** 開始日 (yyyyMMdd)。未確定時は空文字 */
  startYMD: string
  /** 終了日 (yyyyMMdd)。未確定時は空文字 */
  endYMD: string
  /** ひな形一覧の取得が完了しているか */
  templatesLoaded: boolean
  /** ひな形 ID から名前を引く */
  resolveTemplateName: (id: number) => string | undefined
  /** 複数ひな形時に使う固定ラベル */
  bulkLabel: string
  /** 現在の出力ファイル名を読む (毎レンダー購読しないよう getter で受ける) */
  getOutputFileName: () => string
  /** 出力ファイル名を反映する */
  setOutputFileName: (name: string) => void
  /** ファイル名を生成したときに検証を再実行する */
  triggerValidation: () => void
}

const DATE_SUFFIX = /_\d{8}-\d{8}$/

/**
 * 出力ファイル名から末尾の日付範囲 (`_yyyyMMdd-yyyyMMdd`) を除いた前半 (ベース名) を返す。
 * 前半に `_` を含んでも末尾の日付範囲のみ取り除く。
 */
export const extractBaseName = (fileName: string): string =>
  fileName.replace(DATE_SUFFIX, '')

/**
 * 出力ファイル名を `<前半>_<開始日>-<終了日>` 形式で組み立てる。
 *
 * - 日付パーツは常に選択期間に追従する。
 * - 前半パーツは「自動値 (単一ひな形ならひな形名、複数なら {@link Params.bulkLabel})」と
 *   一致する間はひな形/モード変更に追従し、ユーザーがカスタムした名前は維持する。
 *
 * 判定はファイル名フィールドへの直接編集では走らせず (getOutputFileName は購読しない)、
 * 期間・ひな形の変更時にのみ再計算する。
 */
export const useAutoOutputFileName = ({
  templateIds,
  startYMD,
  endYMD,
  templatesLoaded,
  resolveTemplateName,
  bulkLabel,
  getOutputFileName,
  setOutputFileName,
  triggerValidation,
}: Params): void => {
  // 直前に適用した自動値。ひな形/モード変更時に「以前は自動値だった」前半を
  // 追従させるために保持する。
  const prevAutoBaseRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!startYMD || !endYMD) return

    if (templateIds.length === 0) {
      // 直前まで自動入力されていたひな形名がそのまま送信されるのを防ぐ
      setOutputFileName('')
      prevAutoBaseRef.current = undefined
      return
    }

    // 単一ひな形は名前が解決できるまで待つ (読込中は確定しない)
    if (templateIds.length === 1 && !templatesLoaded) return
    const [firstTemplateId] = templateIds
    const autoBase =
      templateIds.length === 1 && firstTemplateId != null
        ? (resolveTemplateName(firstTemplateId) ?? '')
        : bulkLabel
    if (autoBase === '') return

    const currentBase = extractBaseName(getOutputFileName())
    // 前半が (空 / 現在の自動値 / 直前の自動値) のいずれかなら、まだ自動とみなして
    // 新しい自動値に追従する。それ以外はユーザーがカスタムした前半なので維持する。
    const base =
      currentBase === '' ||
      currentBase === autoBase ||
      currentBase === prevAutoBaseRef.current
        ? autoBase
        : currentBase

    prevAutoBaseRef.current = autoBase
    setOutputFileName(`${base}_${startYMD}-${endYMD}`)
    triggerValidation()
  }, [
    bulkLabel,
    endYMD,
    getOutputFileName,
    resolveTemplateName,
    setOutputFileName,
    startYMD,
    templateIds,
    templatesLoaded,
    triggerValidation,
  ])
}
